import { useEffect, useRef, useState } from 'react';
import { Alert, App, Button, Form, Input, Modal, Select, Typography } from 'antd';
import type { AssignMembershipRequest, MembershipAssignment } from '@goelectric/shared-types';
import { BILLING_CYCLE_LABELS } from '@/features/membresias/utils/format';
import { createIdempotencyKey } from '../../api/customerApi';
import { asignacionesApi } from '../api/asignacionesApi';
import { useAssignableMemberships } from '../hooks/useAssignableCatalog';
import { describeAssignmentError } from '../utils/errors';
import { formatCop, formatDiscountPct } from '../utils/format';

interface AssignMembershipFormValues {
  membershipId: string;
  note?: string;
}

export interface AssignMembershipModalProps {
  open: boolean;
  customerId: string;
  customerName?: string;
  /** Nombre de la membresía activa cuando la operación la reemplaza. */
  activeName?: string;
  onClose: () => void;
  onAssigned: (assignment: MembershipAssignment) => void;
}

/**
 * Modal **Asignar / Reemplazar membresía** (US-2.4, TG-53).
 *
 * El contrato expone una sola membresía activa por cliente. Si ya hay una, el
 * `PUT` la cancela y crea la nueva en una transacción; el modal avisa explícitamente
 * que la anterior quedará en el historial. Envía `Idempotency-Key` por intención.
 */
export function AssignMembershipModal({
  open,
  customerId,
  customerName,
  activeName,
  onClose,
  onAssigned,
}: AssignMembershipModalProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm<AssignMembershipFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const catalog = useAssignableMemberships(open);
  const idempotencyKeyRef = useRef<string | null>(null);
  const isReplacing = Boolean(activeName);

  useEffect(() => {
    if (open) {
      setError(null);
      form.resetFields();
      idempotencyKeyRef.current = createIdempotencyKey();
    } else {
      idempotencyKeyRef.current = null;
    }
  }, [open, form]);

  const handleCancel = () => {
    idempotencyKeyRef.current = null;
    onClose();
  };

  const handleFinish = async (values: AssignMembershipFormValues) => {
    setSubmitting(true);
    setError(null);
    const body: AssignMembershipRequest = {
      membershipId: values.membershipId,
      note: values.note?.trim() || undefined,
    };
    try {
      const idempotencyKey =
        idempotencyKeyRef.current ?? (idempotencyKeyRef.current = createIdempotencyKey());
      const created = await asignacionesApi.assignMembership(customerId, body, idempotencyKey);
      idempotencyKeyRef.current = null;
      message.success(
        isReplacing
          ? 'Membresía reemplazada. La anterior quedó cancelada en el historial.'
          : `Membresía asignada${customerName ? ` a ${customerName}` : ''}.`,
      );
      onAssigned(created);
      onClose();
    } catch (caught) {
      setError(describeAssignmentError(caught, 'No se pudo asignar la membresía.'));
    } finally {
      setSubmitting(false);
    }
  };

  const options = catalog.items.map((membership) => ({
    value: membership.id,
    label: `${membership.name} — ${BILLING_CYCLE_LABELS[membership.billingCycle]} — ${formatDiscountPct(membership.tariffDiscountPct)} — ${formatCop(membership.priceCop, membership.currency || 'COP')}`,
  }));

  return (
    <Modal
      open={open}
      title={isReplacing ? 'Reemplazar membresía' : 'Asignar membresía'}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      okText={isReplacing ? 'Reemplazar membresía' : 'Asignar membresía'}
      cancelText="Cancelar"
      confirmLoading={submitting}
      destroyOnHidden
      keyboard
    >
      {error && (
        <Alert
          type="error"
          showIcon
          role="alert"
          message="No se pudo asignar la membresía"
          description={error}
          style={{ marginBottom: 16 }}
        />
      )}

      {isReplacing && (
        <Alert
          type="warning"
          showIcon
          message="La membresía actual quedará cancelada"
          description={`«${activeName}» pasará al historial del cliente y la nueva membresía será la única activa. Esta operación se registra en auditoría.`}
          style={{ marginBottom: 16 }}
        />
      )}

      <Typography.Paragraph>
        Solo se ofrecen membresías activas del catálogo global de la plataforma.
      </Typography.Paragraph>

      {catalog.error && (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar el catálogo de membresías"
          description={catalog.error}
          action={
            <Button size="small" onClick={catalog.refresh}>
              Reintentar
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {!catalog.loading && !catalog.error && options.length === 0 && (
        <Alert
          type="info"
          showIcon
          message="No hay membresías asignables"
          description="Activa una membresía en el catálogo antes de asignarla a un cliente."
          style={{ marginBottom: 16 }}
        />
      )}

      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          label="Membresía"
          name="membershipId"
          rules={[{ required: true, message: 'Selecciona la membresía a asignar.' }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            loading={catalog.loading}
            options={options}
            placeholder="Selecciona una membresía activa"
            aria-label="Membresía a asignar"
            notFoundContent={catalog.loading ? 'Cargando membresías…' : 'Sin membresías asignables'}
          />
        </Form.Item>

        <Form.Item label="Nota (interna)" name="note">
          <Input.TextArea
            rows={3}
            maxLength={500}
            placeholder="Motivo del cambio o referencia (visible en auditoría)."
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
